import type {
  BitbucketWorkspace,
  ConsentInfo,
} from '#definitions/consent-info';

/** The parts of `/consent/info` that map a site back to its organization. */
export type OrgMatchInfo = Pick<ConsentInfo, 'availableOrganizations'>;

/** Site URL -> owning orgId, as built by `buildSiteOrgIndex`. */
export type SiteOrgIndex = ReadonlyMap<string, string>;

/**
 * Compare site URLs on their bare host.
 *
 * `availableWorkspaces` and `availableSites[].url` are bare hosts, while
 * `units[].workspaces[].workspaceUrl` carries a scheme, so the two sources only
 * line up once the scheme and any trailing slash are stripped.
 */
function normalizeSiteUrl(rawUrl: string | null | undefined): string | null {
  const url = rawUrl
    ?.trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  return url ? url.toLowerCase() : null;
}

/**
 * Index every site URL `/consent/info` attributes to an organization.
 *
 * `availableWorkspaces` is the authoritative per-org site list, so it is read
 * first and wins; `units[].workspaces[]` is read as a fallback for responses
 * that populate the units tree but not the flat list. A URL already indexed is
 * never overwritten, so the first org to claim a site keeps it.
 */
export function buildSiteOrgIndex(info: OrgMatchInfo): SiteOrgIndex {
  const index = new Map<string, string>();

  const claim = (rawUrl: string | null | undefined, orgId: string) => {
    const url = normalizeSiteUrl(rawUrl);
    if (url && !index.has(url)) {
      index.set(url, orgId);
    }
  };

  for (const org of info.availableOrganizations ?? []) {
    if (!org?.orgId) continue;
    for (const workspace of org.availableWorkspaces ?? []) {
      claim(workspace, org.orgId);
    }
  }

  for (const org of info.availableOrganizations ?? []) {
    if (!org?.orgId) continue;
    for (const unit of org.units ?? []) {
      for (const workspace of unit.workspaces ?? []) {
        claim(workspace.workspaceUrl, org.orgId);
        claim(workspace.cloudUrl, org.orgId);
      }
    }
  }

  return index;
}

/** Orgs whose consent may hold several sites / Bitbucket workspaces per picker. */
export function buildMultiSelectOrgIds(
  info: OrgMatchInfo,
): ReadonlySet<string> {
  return new Set(
    (info.availableOrganizations ?? [])
      .filter((org) => org?.orgId && org.multiSiteSelectionEnabled === true)
      .map((org) => org.orgId),
  );
}

/**
 * Bare hosts of the sandbox sites in `/consent/info`. Only
 * `units[].workspaces[]` carries the flag, so a site is a sandbox when any of
 * its workspaces says so.
 */
export function buildSandboxSiteUrls(info: OrgMatchInfo): ReadonlySet<string> {
  const urls = (info.availableOrganizations ?? [])
    .flatMap((org) => org?.units ?? [])
    .flatMap((unit) => unit.workspaces ?? [])
    .filter((workspace) => workspace.sandbox)
    .flatMap((workspace) => [workspace.workspaceUrl, workspace.cloudUrl])
    .map(normalizeSiteUrl);
  return new Set(urls.filter((url): url is string => url != null));
}

export function isSandboxSite(
  sandboxSiteUrls: ReadonlySet<string>,
  siteUrl: string | null | undefined,
): boolean {
  const url = normalizeSiteUrl(siteUrl);
  return url != null && sandboxSiteUrls.has(url);
}

/**
 * The organization owning a selected site, or null when the response does not
 * say. Null means "unknown", which callers treat as "no conflict" rather than
 * as a mismatch — see `isOrgMismatch`.
 */
export function resolveSiteOrgId(
  index: SiteOrgIndex,
  siteUrl: string | null | undefined,
): string | null {
  const url = normalizeSiteUrl(siteUrl);
  return (url && index.get(url)) || null;
}

/** The organization heading an option is listed under in a grouped picker. */
export type OrgGroup = { orgId: string; name: string };

/** Heads the options of no named org, once any option has one. */
export const NO_ORG_GROUP = 'no-org';

/**
 * Where `groupByOrg` lists an item: under its org, under the no-organization heading,
 * or (null) in a flat list with no headings at all.
 */
export type ItemOrgGroup = OrgGroup | typeof NO_ORG_GROUP | null;

/**
 * Items ordered under their organization, for a picker that groups its
 * options by org.
 *
 * Each item's org comes from `getOrgId`, the same lookup the org rules read,
 * so a heading never disagrees with which items can be picked together. Only a
 * named org makes a heading: an item whose org is unknown or unnamed is listed
 * after the groups under the no-organization heading. When no item lands in a named
 * org there is nothing to tell apart, so the items come back in their original
 * order with no headings — as for a response without `availableOrganizations`.
 *
 * Groups follow the response's org order, or org name when `sortGroups` is
 * set. Items keep their incoming order within a group.
 */
export function groupByOrg<TItem extends object>(
  items: readonly TItem[],
  info: OrgMatchInfo,
  getOrgId: (item: TItem) => string | null,
  { sortGroups = false }: { sortGroups?: boolean } = {},
): (TItem & { orgGroup: ItemOrgGroup })[] {
  const groups: OrgGroup[] = [];
  for (const org of info.availableOrganizations ?? []) {
    const name = org?.name?.trim();
    if (org?.orgId && name && !groups.some((g) => g.orgId === org.orgId)) {
      groups.push({ orgId: org.orgId, name });
    }
  }
  if (sortGroups) {
    groups.sort((a, b) => a.name.localeCompare(b.name));
  }

  const grouped = items.map((item) => {
    const orgId = getOrgId(item);
    return {
      ...item,
      orgGroup: groups.find((group) => group.orgId === orgId) ?? null,
    };
  });
  // The no-organization heading only means something beside a named one; without
  // any, the list stays flat.
  const ungroupedOrgGroup: ItemOrgGroup = grouped.some(
    (item) => item.orgGroup != null,
  )
    ? NO_ORG_GROUP
    : null;
  return [
    ...groups.flatMap((group) =>
      grouped.filter((item) => item.orgGroup === group),
    ),
    ...grouped
      .filter((item) => item.orgGroup == null)
      .map((item) => ({ ...item, orgGroup: ungroupedOrgGroup })),
  ];
}

/** `groupByOrg` for sites, attributed through `siteOrgIndex`. */
export const groupSitesByOrg = <TSite extends { url: string }>(
  sites: readonly TSite[],
  info: OrgMatchInfo,
  siteOrgIndex: SiteOrgIndex,
  options?: { sortGroups?: boolean },
) =>
  groupByOrg(
    sites,
    info,
    (site) => resolveSiteOrgId(siteOrgIndex, site.url),
    options,
  );

/**
 * MultiSelect `getGroup` for items placed by `groupByOrg`; `noOrgLabel`
 * heads the options of no named org.
 */
export const getOrgGroupHeading = (
  { orgGroup }: { orgGroup?: ItemOrgGroup },
  noOrgLabel: string,
) => {
  if (orgGroup === NO_ORG_GROUP) {
    return { key: NO_ORG_GROUP, label: noOrgLabel };
  }
  return orgGroup ? { key: orgGroup.orgId, label: orgGroup.name } : null;
};

/** The organization owning a Bitbucket workspace, or null when unknown. */
export const getBitbucketWorkspaceOrgId = (
  workspace: Pick<BitbucketWorkspace, 'orgId'>,
): string | null => workspace.orgId ?? null;

/**
 * Whether two selections are in different organizations.
 *
 * A consent grants one organization's data, so the two cannot be granted
 * together. Only compared when both are known: a missing selection, or a site
 * we cannot attribute to an org, fails open rather than blocking a combination
 * we cannot actually judge.
 */
export function isOrgMismatch(
  orgId: string | null,
  otherOrgId: string | null,
): boolean {
  return orgId != null && otherOrgId != null && orgId !== otherOrgId;
}

/** Which org helper a picker shows below its field. */
export type OrgHelpKind = 'none' | 'constraint' | 'empty';

/**
 * How the constraining org (see useOrgMatch) limits a picker: which options to
 * disable, and which helper to explain it with. Shared by the site and
 * Bitbucket workspace pickers.
 *
 * `constraint` says only same-org options are selectable, so it is only worth
 * showing while something is still pickable — once every option is other-org
 * that line is a dead end, and `empty` says none are available instead. In
 * single-select a made pick retires the helper, as picking again replaces it;
 * in multi-select the field's own picks constrain it, so the helper stays.
 * Unknown orgs fail open, exactly as `isOrgMismatch` does.
 */
export function getOrgOptionState<TItem>({
  items,
  constraintOrgId,
  getOrgId,
  canShowHelp,
  hasOwnSelection = false,
  isMultiSelect = false,
}: {
  items: readonly TItem[];
  /** The org options have to match, or null when unconstrained. */
  constraintOrgId: string | null;
  getOrgId: (item: TItem) => string | null;
  /** False where a higher-priority message already owns the slot. */
  canShowHelp: boolean;
  /** Whether the picker already holds a selection. */
  hasOwnSelection?: boolean;
  isMultiSelect?: boolean;
}): {
  /** Hand to MultiSelect's `isItemDisabled`; undefined leaves every option enabled. */
  isItemDisabled?: (item: TItem) => boolean;
  helpKind: OrgHelpKind;
} {
  if (!constraintOrgId) {
    return { helpKind: 'none' };
  }
  // Multi-select shares one policy check across its selections, so an option
  // whose org is unknown cannot be vouched for and fails closed; single-select
  // replaces its pick and keeps failing open.
  const isItemDisabled = (item: TItem): boolean => {
    const orgId = getOrgId(item);
    return isMultiSelect
      ? orgId !== constraintOrgId
      : isOrgMismatch(orgId, constraintOrgId);
  };
  const disabledCount = items.filter(isItemDisabled).length;
  const showsHelp = canShowHelp && (isMultiSelect || !hasOwnSelection);
  return {
    isItemDisabled,
    helpKind:
      !showsHelp || disabledCount === 0
        ? 'none'
        : disabledCount === items.length
          ? 'empty'
          : 'constraint',
  };
}

/**
 * What the current selections allow, as pure rules over the indexes so they
 * can be read without React — see useOrgMatch for how the pickers use them.
 */
export function getSelectionRules({
  isMultiSelect,
  selectedSiteUrls,
  hasSelectedWorkspaces,
  selectionOrgId,
  multiSelectOrgIds,
  sandboxSiteUrls,
}: {
  isMultiSelect: boolean;
  /** Selected sites of a picker that is on screen. */
  selectedSiteUrls: readonly string[];
  /** Whether a Bitbucket workspace of a picker on screen is selected. */
  hasSelectedWorkspaces: boolean;
  /** The first known org among the selections, or null. */
  selectionOrgId: string | null;
  multiSelectOrgIds: ReadonlySet<string>;
  sandboxSiteUrls: ReadonlySet<string>;
}): {
  isMultiSelectionAllowed: boolean;
  siteSandboxConstraint: boolean | null;
  isBitbucketBlockedBySandbox: boolean;
  hasSandboxConflict: boolean;
} {
  const hasSelection = selectedSiteUrls.length > 0 || hasSelectedWorkspaces;
  const sandboxFlags = selectedSiteUrls.map((url) =>
    isSandboxSite(sandboxSiteUrls, url),
  );
  const isBlockedBySandbox = isMultiSelect && sandboxFlags.some(Boolean);
  return {
    // Nothing selected yet, so no org to ask; afterwards an org not known to
    // allow it fails closed.
    isMultiSelectionAllowed:
      isMultiSelect &&
      (!hasSelection ||
        (selectionOrgId != null && multiSelectOrgIds.has(selectionOrgId))),
    // Sandbox rules only apply where selection is multi, like the org rules: a
    // sandbox consent is sandbox sites of one org alone, so the first site sets
    // the kind; a picked workspace means production.
    siteSandboxConstraint: isMultiSelect
      ? sandboxFlags.length > 0
        ? sandboxFlags[0]
        : hasSelectedWorkspaces
          ? false
          : null
      : null,
    isBitbucketBlockedBySandbox: isBlockedBySandbox,
    // Both selected, so the pickers' own guards were bypassed (e.g. a prefilled
    // site): Accept is blocked until one is removed.
    hasSandboxConflict: isBlockedBySandbox && hasSelectedWorkspaces,
  };
}
